import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword,
  updateProfile,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import AutoLinkingService from './autoLinkingService';
import {
  LoginForm,
  ParentRegisterForm,
  RegisterForm,
  StudentRegisterForm,
  TeacherRegisterForm,
  User,
} from '../types';
import {
  isAccountDisabled,
  isSystemManager,
} from '../utils/authorization';

const googleProvider = new GoogleAuthProvider();

const isConfiguredSystemManager = async (
  userId: string,
  user: User
): Promise<boolean> => {
  if (user.role !== 'admin') return true;
  if (!isSystemManager(user)) return false;

  try {
    const securitySnapshot = await getDoc(
      doc(db, 'systemSettings', 'security')
    );
    return (
      securitySnapshot.exists() &&
      securitySnapshot.data().systemManagerUid === userId
    );
  } catch (error) {
    console.error('System manager verification failed:', error);
    return false;
  }
};

export interface AuthResult {
  user: User | null;
  error?: string;
}

export const registerUser = async (
  formData:
    | RegisterForm
    | StudentRegisterForm
    | ParentRegisterForm
    | TeacherRegisterForm
): Promise<AuthResult> => {
  try {
    const { email, password, name, role, phone } = formData;

    if (!['student', 'parent', 'teacher'].includes(role)) {
      throw new Error('نوع المستخدم غير مدعوم.');
    }

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const firebaseUser = userCredential.user;
    await updateProfile(firebaseUser, { displayName: name });

    const userData: Partial<User> = {
      id: firebaseUser.uid,
      email: firebaseUser.email || email,
      name,
      phone: phone || '',
      role,
      ...(firebaseUser.photoURL && { photoURL: firebaseUser.photoURL }),
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const linkingCode = await AutoLinkingService.createUserLinkingCode(
      firebaseUser.uid,
      role
    );
    let teacherMirrorDoc: Record<string, unknown> | null = null;

    if (role === 'teacher') {
      const teacherData = formData as TeacherRegisterForm;
      const specialization = teacherData.specialization || teacherData.subjects || [];
      userData.teacherProfile = {
        bio: teacherData.bio,
        subjects: specialization,
        pricePerHour: teacherData.pricePerHour,
        currency: 'EGP',
        subscriptionLevel: 'basic',
        qualifications: teacherData.qualifications || [],
        experience: teacherData.experience || 0,
        rating: 0,
        totalStudents: 0,
        totalSessions: 0,
        teacherCode: linkingCode,
      };

      teacherMirrorDoc = {
        name,
        email: firebaseUser.email || email,
        phone: phone || '',
        photoURL: firebaseUser.photoURL || '',
        specialization,
        bio: teacherData.bio || '',
        experience: teacherData.experience || 0,
        qualifications: teacherData.qualifications || [],
        pricePerHour: teacherData.pricePerHour || 0,
        linkingCode,
        rating: 0,
        totalStudents: 0,
        totalClasses: 0,
        isActive: true,
        isVerified: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
    }

    if (role === 'student') {
      const studentData = formData as StudentRegisterForm;
      userData.studentProfile = {
        grade: studentData.grade || '',
        enrolledClasses: [],
        totalHours: 0,
        averageGrade: 0,
        studentCode: linkingCode,
        parentContactInfo: {
          name: '',
          phone: studentData.parentPhone || '',
          email: studentData.parentEmail || '',
        },
      };

      await AutoLinkingService.linkStudentToDefaultTeacher(firebaseUser.uid);

      if (studentData.parentCode) {
        await AutoLinkingService.autoLinkStudentToParent(
          firebaseUser.uid,
          studentData.parentCode,
          undefined,
          undefined
        );
      }
    }

    if (role === 'parent') {
      const parentData = formData as ParentRegisterForm;
      userData.parentProfile = {
        children: [],
        parentCode: linkingCode,
      };

      if (parentData.studentCodes?.length) {
        for (const studentCode of parentData.studentCodes) {
          if (studentCode?.trim()) {
            await AutoLinkingService.autoLinkParentToStudent(
              firebaseUser.uid,
              studentCode.trim()
            );
          }
        }
      }
    }

    const userDocument = {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (teacherMirrorDoc) {
      const batch = writeBatch(db);
      batch.set(doc(db, 'users', firebaseUser.uid), userDocument);
      batch.set(doc(db, 'teachers', firebaseUser.uid), teacherMirrorDoc);
      await batch.commit();
    } else {
      await setDoc(doc(db, 'users', firebaseUser.uid), userDocument);
    }

    return { user: userData as User };
  } catch (error: any) {
    console.error('Registration error:', error);
    return {
      user: null,
      error: getAuthErrorMessage(error.code) || error.message,
    };
  }
};

export const loginUser = async (formData: LoginForm): Promise<AuthResult> => {
  try {
    const { email, password } = formData;
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const firebaseUser = userCredential.user;

    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (!userDoc.exists()) {
      await signOut(auth);
      return { user: null, error: 'بيانات المستخدم غير موجودة.' };
    }

    const userData = userDoc.data() as User;
    if (!(await isConfiguredSystemManager(firebaseUser.uid, userData))) {
      await signOut(auth);
      return {
        user: null,
        error: 'حساب مدير النظام غير معتمد أو غير نشط.',
      };
    }
    if (isAccountDisabled(userData)) {
      await signOut(auth);
      return {
        user: null,
        error: 'هذا الحساب موقوف. تواصل مع الإدارة.',
      };
    }

    return { user: userData };
  } catch (error: any) {
    console.error('Login error:', error);
    return {
      user: null,
      error: getAuthErrorMessage(error.code),
    };
  }
};

export const loginWithGoogle = async (): Promise<AuthResult> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const firebaseUser = result.user;
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

    if (userDoc.exists()) {
      const userData = userDoc.data() as User;
      if (!(await isConfiguredSystemManager(firebaseUser.uid, userData))) {
        await signOut(auth);
        return {
          user: null,
          error: 'حساب مدير النظام غير معتمد أو غير نشط.',
        };
      }
      if (isAccountDisabled(userData)) {
        await signOut(auth);
        return {
          user: null,
          error: 'هذا الحساب موقوف. تواصل مع الإدارة.',
        };
      }
      return { user: userData };
    }

    await signOut(auth);
    return {
      user: null,
      error:
        'لا يوجد حساب مستخدم مرتبط بهذا البريد. استخدم حسابًا مسجلًا أو تواصل مع الإدارة.',
    };
  } catch (error: any) {
    console.error('Google login error:', error);
    return {
      user: null,
      error: getAuthErrorMessage(error.code),
    };
  }
};

export const logoutUser = async (): Promise<void> => {
  await signOut(auth);
};

export const resetPassword = async (
  email: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error: any) {
    console.error('Reset password error:', error);
    return {
      success: false,
      error: getAuthErrorMessage(error.code),
    };
  }
};

export const updateUserProfile = async (
  userId: string,
  updates: Partial<User>
): Promise<{ success: boolean; error?: string }> => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    if (auth.currentUser && (updates.name || updates.photoURL)) {
      await updateProfile(auth.currentUser, {
        displayName: updates.name,
        photoURL: updates.photoURL,
      });
    }

    return { success: true };
  } catch (error: any) {
    console.error('Update profile error:', error);
    return {
      success: false,
      error: 'فشل في تحديث الملف الشخصي.',
    };
  }
};

export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!auth.currentUser?.email) {
      throw new Error('المستخدم غير مسجل الدخول.');
    }

    const credential = EmailAuthProvider.credential(
      auth.currentUser.email,
      currentPassword
    );
    await reauthenticateWithCredential(auth.currentUser, credential);
    await updatePassword(auth.currentUser, newPassword);

    return { success: true };
  } catch (error: any) {
    console.error('Change password error:', error);
    return {
      success: false,
      error: getAuthErrorMessage(error.code) || error.message,
    };
  }
};

export const getCurrentUser = async (): Promise<User | null> => {
  try {
    if (!auth.currentUser) return null;

    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    return userDoc.exists() ? (userDoc.data() as User) : null;
  } catch (error) {
    console.error('Get current user error:', error);
    return null;
  }
};

export const createTeacherAccount = async (teacherData: {
  email: string;
  password: string;
  name: string;
  phone: string;
  bio?: string;
  subjects?: string[];
  qualifications?: string[];
  experience?: number;
  pricePerHour?: number;
}): Promise<{ success: boolean; teacherId?: string; error?: string }> => {
  try {
    await setDoc(doc(db, 'systemSettings', 'main'), {
      teacherName: teacherData.name,
      teacherEmail: teacherData.email,
      teacherPhone: teacherData.phone,
      teacherPhoto: '',
      schoolName: '',
      schoolLogo: '',
      currency: 'EGP',
      timezone: 'Africa/Cairo',
      language: 'ar',
      allowCashPayments: true,
      allowOnlinePayments: false,
      defaultSessionPrice: 100,
      emailNotifications: true,
      smsNotifications: true,
      pushNotifications: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      version: '1.0.0',
    });

    return { success: true, teacherId: 'main' };
  } catch (error: any) {
    console.error('Create teacher account error:', error);
    return {
      success: false,
      error: 'فشل في إنشاء حساب المدرس.',
    };
  }
};

export const initializeSystem = async (): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const settingsRef = doc(db, 'systemSettings', 'main');
    const settingsDoc = await getDoc(settingsRef);
    if (settingsDoc.exists()) return { success: true };

    await setDoc(settingsRef, {
      teacherName: 'المعلم الافتراضي',
      teacherEmail: 'teacher@class-tech.com',
      teacherPhone: '',
      teacherPhoto: '',
      schoolName: 'منصة Class-Tech',
      schoolLogo: '',
      currency: 'EGP',
      timezone: 'Africa/Cairo',
      language: 'ar',
      allowCashPayments: true,
      allowOnlinePayments: false,
      defaultSessionPrice: 100,
      emailNotifications: true,
      smsNotifications: true,
      pushNotifications: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      version: '1.0.0',
    });

    return { success: true };
  } catch (error: any) {
    console.error('Initialize system error:', error);
    return {
      success: false,
      error: 'فشل في تهيئة النظام.',
    };
  }
};

const getAuthErrorMessage = (errorCode?: string): string => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'البريد الإلكتروني مستخدم بالفعل.';
    case 'auth/invalid-email':
      return 'البريد الإلكتروني غير صحيح.';
    case 'auth/operation-not-allowed':
      return 'العملية غير مسموحة.';
    case 'auth/weak-password':
      return 'كلمة المرور ضعيفة جدًا.';
    case 'auth/user-disabled':
      return 'الحساب معطل.';
    case 'auth/user-not-found':
    case 'auth/invalid-credential':
      return 'بيانات تسجيل الدخول غير صحيحة.';
    case 'auth/wrong-password':
      return 'كلمة المرور غير صحيحة.';
    case 'auth/too-many-requests':
      return 'محاولات كثيرة، يرجى المحاولة لاحقًا.';
    case 'auth/network-request-failed':
      return 'تعذر الاتصال بالإنترنت.';
    case 'auth/requires-recent-login':
      return 'يتطلب هذا الإجراء تسجيل دخول حديث.';
    case 'appCheck/recaptcha-error':
      return 'تعذر التحقق من App Check لهذا النطاق. تأكد من إعداد مفتاح reCAPTCHA/App Check للنطاق الحالي.';
    case 'permission-denied':
      return 'تعذر قراءة بيانات الحساب. تأكد من إعداد App Check وقواعد Firestore للنطاق الحالي.';
    default:
      return 'حدث خطأ غير متوقع.';
  }
};
